/**
 * Fim do PRO — a decisão de "avisar ou não", pura, e o registro de "já viu".
 *
 * Blueprint: `docs/systems/promo-pro-days.md` §10. Nasceu com o cupom da
 * Bienal (BIENAL15), mas vale para QUALQUER lapso de PRO — cortesia que
 * expirou, assinatura cancelada no fim do período, trial não convertido. É o
 * primeiro momento de reengajamento pós-lapso que o produto tem, e a decisão
 * de mantê-lo genérico está registrada no blueprint §16.
 *
 * ## Por que 100% client, sem campo servidor
 *
 * Um `users.proLapse` exigiria dar ao `entitlementResolver` — a peça mais
 * protegida do billing, com gate de "a suíte passa sem editar uma asserção" —
 * um parâmetro novo para detectar a TRANSIÇÃO true→false. Desproporcional, e
 * não resolveria o dedup de exibição, que é client de qualquer jeito.
 *
 * O dado já está na mão: `users.proExpiresAt` é escrito INCONDICIONALMENTE
 * pelo resolver (sobrevive à revogação), então `isPro === false` com
 * `proExpiresAt` no passado é exatamente "teve PRO e acabou".
 *
 * ## O que se guarda é o VALOR, não um booleano
 *
 * A chave de "já viu" é o `proExpiresAt` visto. Quem reassinar e lapsar de
 * novo tem outro valor, e o aviso pode aparecer de novo — sem ninguém precisar
 * limpar nada na reassinatura. Por conta (uid), como o `leagueIntro`: duas
 * contas no mesmo telefone são duas pessoas.
 *
 * ## Storage indisponível
 *
 * A leitura devolve `true` — o lado seguro é NÃO interromper de novo. Sem onde
 * gravar, o aviso voltaria a cada abertura, que é o desfecho pior.
 */

import { resolveCompOrigin } from "./compOrigin";

const KEY_PREFIX = "livra_pro_lapse_seen_";

/** Espelho em memória — cobre `localStorage` que lê mas não escreve. */
const seenInSession = new Map<string, number>();

function storageKey(uid: string): string {
  return `${KEY_PREFIX}${uid}`;
}

export function hasSeenProLapse(uid: string, proExpiresAt: number): boolean {
  if (!uid) return true;
  if (seenInSession.get(uid) === proExpiresAt) return true;
  try {
    return window.localStorage.getItem(storageKey(uid)) === String(proExpiresAt);
  } catch {
    return true;
  }
}

export function markProLapseSeen(uid: string, proExpiresAt: number): void {
  if (!uid) return;
  seenInSession.set(uid, proExpiresAt);
  try {
    window.localStorage.setItem(storageKey(uid), String(proExpiresAt));
  } catch {
    /* espelho em memória cobre a sessão */
  }
}

/** Só para testes. */
export function __resetProLapseForTests(): void {
  seenInSession.clear();
}

/**
 * A checagem BARATA (síncrona, sobre dado já em memória). Nenhum read.
 *
 * Quem nunca teve PRO tem `proExpiresAt` nulo; o vitalício também (o resolver
 * nunca o seta). Quem AINDA é PRO nunca entra — a condição é sempre
 * `isPro === false`, e é o que impede o aviso de mentir "acabou" durante a
 * janela de até ~24h entre `currentPeriodEnd` e o cron que revoga.
 */
export function isProLapseCandidate(args: {
  isPro: boolean | null | undefined;
  proExpiresAt: number | null | undefined;
  nowMs: number;
}): boolean {
  const { isPro, proExpiresAt, nowMs } = args;
  if (isPro !== false) return false;
  if (typeof proExpiresAt !== "number" || !Number.isFinite(proExpiresAt)) return false;
  return proExpiresAt < nowMs;
}

export type ProLapseOrigin = "promo" | "creator" | "granted" | "subscription";

/**
 * De onde era o PRO que acabou — decide a copy. Lido do doc
 * `subscriptions/{uid}` (owner-readable), porque `users.proSource` é ZERADO
 * pelo resolver no instante em que `isPro` cai.
 *
 * `null` = não houve PRO de verdade a lamentar: doc ausente (apagado — o
 * resolver zera tudo) ou checkout que nunca virou assinatura (`incomplete`,
 * que pode carregar um `currentPeriodEnd` sem nunca ter concedido nada).
 * Nesses casos o aviso NÃO abre — anunciar o fim de algo que não começou é
 * pior que o silêncio.
 */
export function resolveProLapseOrigin(
  sub: { plan?: string | null; status?: string | null; grantReason?: string | null } | null | undefined,
): ProLapseOrigin | null {
  if (!sub) return null;
  if (sub.status === "incomplete") return null;
  // A mesma pergunta que a faixa da `/pro` faz — uma resposta só (`compOrigin`).
  if (sub.plan === "creator_comp") return resolveCompOrigin(sub.grantReason);
  return "subscription";
}

export interface ProLapseNotice {
  origin: ProLapseOrigin;
  /** O `proExpiresAt` que este aviso descreve — é também a chave de "já viu". */
  endedAt: number;
}
